const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.obscurium.pro';
const X_API_KEY = process.env.REACT_APP_X_API_KEY || '35ae663864699ef4409b93ad7440890257b25b06a8299b1823dc4a31e9dccf73';

// Get auth token
const getAuthToken = (): string | null => {
  try {
    const stored = sessionStorage.getItem('OBSC_JWT');
    if (stored) return stored;
  } catch {
    // ignore storage errors
  }
  return process.env.REACT_APP_API_KEY || null;
};

// API helper function
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(X_API_KEY ? { 'X-API-KEY': X_API_KEY } : {}),
    ...(options.headers as Record<string, string> || {}),
  };

  // Add auth token if available and not already present
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export interface BackendToken {
  address: string;
  symbol: string;
  name: string;
  color: string;
  logo: string;
  decimals: number;
  isNative: boolean;
}

export interface BackendChain {
  id: string;
  chainId: number;
  name: string;
  description: string;
  logo: string;
  nativeCurrency: {
    symbol: string;
    name: string;
    decimals: number;
  };
  tokens?: BackendToken[];
}

export interface ChainsResponse {
  status: string;
  data: {
    chains: BackendChain[];
  };
  message: string;
}

export interface TokensResponse {
  status: string;
  data: {
    chainId: string;
    chainName: string;
    tokens: BackendToken[];
  };
  message: string;
}

// Fetch all chains
export const fetchAllChains = async (): Promise<BackendChain[]> => {
  const response: ChainsResponse = await apiRequest('/v1/config/chains');
  return response.data.chains;
};

// Fetch chains for transfer
export const fetchTransferChains = async (): Promise<BackendChain[]> => {
  const response: ChainsResponse = await apiRequest('/v1/config/chains/transfer');
  return response.data.chains;
};

// Fetch chains for trade
export const fetchTradeChains = async (): Promise<BackendChain[]> => {
  const response: ChainsResponse = await apiRequest('/v1/config/chains/trade');
  return response.data.chains;
};

// Fetch chains for spark
export const fetchSparkChains = async (): Promise<BackendChain[]> => {
  const response: ChainsResponse = await apiRequest('/v1/config/chains/spark');
  return response.data.chains;
};

// Fetch tokens for a specific chain
export const fetchTokensForChain = async (chainId: string): Promise<BackendToken[]> => {
  const response: TokensResponse = await apiRequest(`/v1/config/tokens/${chainId}`);
  return response.data.tokens;
};

// Convert backend chain to frontend network format
export const convertChainToNetwork = (chain: BackendChain) => ({
  id: chain.id,
  name: chain.name,
  description: chain.description,
  logo: chain.logo,
  nativeCurrency: chain.nativeCurrency,
});

// Convert backend token to frontend token format
export const convertToken = (token: BackendToken, chainId?: string) => ({
  id: `${token.symbol.toLowerCase()}_${chainId || ''}`,
  symbol: token.symbol,
  name: token.name,
  color: token.color,
  logo: token.logo,
  address: token.address,
  decimals: token.decimals,
  isNative: token.isNative,
  network: chainId,
});

// Fetch token information by address
export const fetchTokenByAddress = async (address: string, chainId: string = 'ethereum'): Promise<BackendToken> => {
  const response = await apiRequest(`/v1/token/info?address=${address}&chain=${chainId}`);

  if (response.status !== 'success') {
    throw new Error(response.message || 'Failed to fetch token info');
  }

  // Convert response to BackendToken format
  return {
    address: response.data.address,
    symbol: response.data.symbol,
    name: response.data.name,
    decimals: response.data.decimals,
    color: '#00e5ff', // Default color for dynamically fetched tokens
    logo: '', // No logo for dynamically fetched tokens
    isNative: false,
  };
};

export { API_BASE_URL };
